import { useState, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, Tooltip as MuiTooltip, CircularProgress } from "@mui/material";
import Grid2 from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { RootState } from "../../../Redux/Store";
import dayjs from "dayjs";
import { FiRefreshCw } from "react-icons/fi";

import {
  useLazyGetDashboardSummaryQuery,
  useGetRevenueTrendMutation,
  useGetPaymentAnalyticsMutation,
  useGetTopProductsMutation,
  useGetCustomerAnalyticsMutation,
  useGetRefundAnalyticsMutation,
  useLazyGetInventoryStatusQuery,
  useGetRecentOrdersMutation,
  useGetDeliveryStatsMutation,
} from "../../../Services/DashboardService";

import DashboardFilters from "./components/DashboardFilters";
import KpiCards from "./components/KpiCards";
import RevenueTrendChart from "./components/RevenueTrendChart";
import PaymentGatewayChart from "./components/PaymentGatewayChart";
import OrderStatusChart from "./components/OrderStatusChart";
import TopProductsChart from "./components/TopProductsChart";
import CustomerGrowthChart from "./components/CustomerGrowthChart";
import RefundAnalyticsChart from "./components/RefundAnalyticsChart";
import InventoryHealthChart from "./components/InventoryHealthChart";
import DeliveryStatsChart from "./components/DeliveryStatsChart";
import RecentOrdersTable from "./components/RecentOrdersTable";

const Dashboard = () => {
  const authUser = useSelector((state: RootState) => state.AuthSlice.adminAuthUser);
  const [activePreset, setActivePreset] = useState(30);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [isRefreshing, setIsRefreshing] = useState(false);

  // API hooks
  const [triggerSummary, { data: summaryResult, isFetching: summaryLoading }] =
    useLazyGetDashboardSummaryQuery();
  const [getRevenueTrend, { data: revenueResult, isLoading: revenueLoading }] =
    useGetRevenueTrendMutation();
  const [getPaymentAnalytics, { data: paymentResult, isLoading: paymentLoading }] =
    useGetPaymentAnalyticsMutation();
  const [getTopProducts, { data: topProductsResult, isLoading: topProductsLoading }] =
    useGetTopProductsMutation();
  const [getCustomerAnalytics, { data: customerResult, isLoading: customerLoading }] =
    useGetCustomerAnalyticsMutation();
  const [getRefundAnalytics, { data: refundResult, isLoading: refundLoading }] =
    useGetRefundAnalyticsMutation();
  const [triggerInventory, { data: inventoryResult, isFetching: inventoryLoading }] =
    useLazyGetInventoryStatusQuery();
  const [getRecentOrders, { data: recentOrdersResult, isLoading: recentOrdersLoading }] =
    useGetRecentOrdersMutation();
  const [getDeliveryStats, { data: deliveryResult, isLoading: deliveryLoading }] =
    useGetDeliveryStatsMutation();

  const buildFilters = useCallback(
    (granularity: "daily" | "weekly" | "monthly" = "daily") => ({
      filters: [
        { key: "startDate", value: startDate },
        { key: "endDate", value: endDate },
      ],
      granularity,
    }),
    [startDate, endDate]
  );

  const fetchAllData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const filters = buildFilters();

      await Promise.all([
        triggerSummary({ startDate, endDate }),
        getRevenueTrend(filters),
        getPaymentAnalytics(filters),
        getTopProducts({ ...filters, limit: 10 }),
        getCustomerAnalytics(filters),
        getRefundAnalytics(filters),
        triggerInventory(),
        getRecentOrders({ limit: 10 }),
        getDeliveryStats(filters),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [
    buildFilters,
    startDate,
    endDate,
    triggerSummary,
    getRevenueTrend,
    getPaymentAnalytics,
    getTopProducts,
    getCustomerAnalytics,
    getRefundAnalytics,
    triggerInventory,
    getRecentOrders,
    getDeliveryStats,
  ]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handlePresetChange = (days: number) => {
    setActivePreset(days);
    const newStart = days === 0 ? dayjs().format("YYYY-MM-DD") : dayjs().subtract(days, "day").format("YYYY-MM-DD");
    setStartDate(newStart);
    setEndDate(dayjs().format("YYYY-MM-DD"));
  };

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
    setActivePreset(-1);
  };

  // Extract data from RTK Query responses
  const summaryData = (summaryResult as any)?.data || null;
  const revenueData = (revenueResult as any)?.data || [];
  const paymentData = (paymentResult as any)?.data || null;
  const topProductsData = (topProductsResult as any)?.data || null;
  const customerData = (customerResult as any)?.data || null;
  const refundData = (refundResult as any)?.data || null;
  const inventoryData = (inventoryResult as any)?.data || null;
  const recentOrdersData = (recentOrdersResult as any)?.data || [];
  const deliveryData = (deliveryResult as any)?.data || null;

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 3,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary" }}>
            Welcome back{authUser?.firstName ? `, ${authUser.firstName}` : ""}!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Here's what's happening with your business
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <DashboardFilters
            startDate={startDate}
            endDate={endDate}
            activePreset={activePreset}
            onPresetChange={handlePresetChange}
            onDateChange={handleDateChange}
          />
          <MuiTooltip title="Refresh data">
            <IconButton onClick={fetchAllData} disabled={isRefreshing} size="small">
              {isRefreshing ? (
                <CircularProgress size={18} />
              ) : (
                <FiRefreshCw size={18} />
              )}
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ mb: 3 }}>
        <KpiCards data={summaryData} loading={summaryLoading} />
      </Box>

      {/* Row 2: Revenue Trend + Payment Gateway */}
      <Grid2 container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <RevenueTrendChart data={revenueData} loading={revenueLoading} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <PaymentGatewayChart data={paymentData} loading={paymentLoading} />
        </Grid2>
      </Grid2>

      {/* Row 3: Order Status + Top Products */}
      <Grid2 container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <OrderStatusChart data={summaryData?.orders} loading={summaryLoading} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <TopProductsChart data={topProductsData} loading={topProductsLoading} />
        </Grid2>
      </Grid2>

      {/* Row 4: Customer Growth + Refund Analytics */}
      <Grid2 container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <CustomerGrowthChart data={customerData} loading={customerLoading} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <RefundAnalyticsChart data={refundData} loading={refundLoading} />
        </Grid2>
      </Grid2>

      {/* Row 5: Inventory Health + Delivery Stats */}
      <Grid2 container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <InventoryHealthChart data={inventoryData} loading={inventoryLoading} />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <DeliveryStatsChart data={deliveryData} loading={deliveryLoading} />
        </Grid2>
      </Grid2>

      {/* Row 6: Recent Orders */}
      <Box sx={{ mb: 2 }}>
        <RecentOrdersTable data={recentOrdersData} loading={recentOrdersLoading} />
      </Box>
    </Box>
  );
};

export default Dashboard;
